import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AuthContext, apiClient } from '../App';
import { useNavigate } from 'react-router-dom';
import { normalizeApiError } from '../utils/normalizeError';
import { beginPushPromptAction } from '../lib/pushPromptManager';
import { toast } from 'sonner';

// Account-scoped, in-memory app-session state; no credentials or persistence.
// Navigation/remount and automatic reads must not undo a capacity rejection.
const capacityRejections = new Map();

// This frontend lineage MUST NOT be deployed to the backend Render service.
export function usePendingInvitations({ onJoined } = {}) {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const identity = useRef({ id: user?.id, token, generation: 0 });
  if (identity.current.id !== user?.id || identity.current.token !== token) {
    identity.current = { id: user?.id, token, generation: identity.current.generation + 1 };
  }
  const generation = identity.current.generation;
  const serial = useRef(0);
  const mounted = useRef(false);
  const [state, setState] = useState({ generation: -1, rows: [], loading: true, error: '' });
  const [busy, setBusy] = useState(null);
  const busyId = busy?.generation === generation ? busy.id : null;
  const [accepted, setAccepted] = useState({ generation: -1, ids: [] });
  const [joinedRows, setJoinedRows] = useState({ generation: -1, rows: [] });
  const [, setCapacityRevision] = useState(0);
  const capacityRejected = row => capacityRejections.get(user?.id)?.has(row.game_id) === true;
  const current = (g, request) => mounted.current && identity.current.generation === g && serial.current === request;
  const refresh = useCallback(async (explicit = false) => {
    const g = identity.current.generation;
    const request = ++serial.current;
    if (!user?.id || !token) return;
    if (explicit) {
      capacityRejections.delete(user.id);
      setCapacityRevision(value => value + 1);
    }
    setState(prev => ({ ...prev, rows: prev.generation === g ? prev.rows : [], generation: g, loading: true, error: '' }));
    try {
      const response = await apiClient.get('/invites/pending');
      if (current(g, request)) setState({ generation: g, rows: response.data.invites || [], loading: false, error: '' });
    } catch (error) {
      if (current(g, request)) setState(prev => ({ generation: g,
        rows: prev.generation === g ? prev.rows.filter(row => capacityRejections.get(user.id)?.has(row.game_id)) : [],
        loading: false, error: normalizeApiError(error, 'Unable to load invitations') }));
    }
  }, [user?.id, token]);
  useEffect(() => {
    mounted.current = true;
    refresh();
    return () => { mounted.current = false; ++serial.current; };
  }, [refresh]);
  const rows = state.generation === generation ? state.rows.filter(row => {
    const raw = String(row.game_date_time || '');
    const start = Date.parse(/(?:Z|[+-]\d{2}:\d{2})$/.test(raw) ? raw : `${raw}Z`);
    return Number.isFinite(start) && start > Date.now()
      && !(accepted.generation === generation && accepted.ids.includes(row.game_id));
  }).filter((row, index, all) => all.findIndex(other => other.game_id === row.game_id) === index) : [];

  const accept = async row => {
    if (!row.can_accept || row.is_full || capacityRejected(row) || busyId) return;
    const raw = String(row.game_date_time || '');
    const start = Date.parse(/(?:Z|[+-]\d{2}:\d{2})$/.test(raw) ? raw : `${raw}Z`);
    if (!Number.isFinite(start) || start <= Date.now()) { refresh(); return; }
    const g = generation;
    const finishPromptAction = beginPushPromptAction();
    setBusy({ generation: g, id: row.id });
    try {
      await apiClient.post(`/invites/${row.id}/respond`, { action: 'accept' });
      if (!mounted.current || identity.current.generation !== g) return;
      setJoinedRows(prev => ({ generation: g, rows: [...(prev.generation === g ? prev.rows : []), row] }));
      setAccepted(prev => ({ generation: g, ids: [...(prev.generation === g ? prev.ids : []), row.game_id] }));
      toast.success('Joined the session');
      onJoined?.();
      refresh();
      navigate(`/games/${row.game_id}`);
      finishPromptAction.accepted();
    } catch (error) {
      if (mounted.current && identity.current.generation === g) {
        const detail = error?.response?.data?.detail;
        if (error?.response?.status === 400 && ['Game is full', 'Both teams are full'].includes(detail)) {
          const rejected = capacityRejections.get(user.id) || new Set();
          rejected.add(row.game_id);
          capacityRejections.set(user.id, rejected);
          setCapacityRevision(value => value + 1);
          toast.error('This session is currently full.');
        } else {
          toast.error(normalizeApiError(error, 'Unable to accept invitation'));
        }
        refresh(); // Capacity/eligibility may have changed since this read.
      }
    } finally {
      finishPromptAction();
      if (mounted.current && identity.current.generation === g) setBusy(null);
    }
  };
  return { rows: user?.id && token ? rows : [],
    joinedRows: joinedRows.generation === generation ? joinedRows.rows : [],
    accept, refresh, busyId, capacityRejected,
    loading: state.generation !== generation || state.loading,
    error: state.generation === generation ? state.error : '' };
}
